import { useEffect, useMemo, useRef } from "react";
import { Gallery, Trash, CloseCircle, DocumentUpload } from "iconsax-react";
import { Button } from "@/shared/ui";
import { ALLOWED_PHOTO_MIME_TYPES } from "../validation";

interface Props {
  existingUrl: string | null;
  pendingFile: File | null;
  markedForDelete: boolean;
  fileError: string | null;
  disabled: boolean;
  labels: {
    title: string;
    hint: string;
    upload: string;
    replace: string;
    delete: string;
    cancel: string;
    empty: string;
  };
  onFileSelected: (file: File) => void;
  onClearPending: () => void;
  onMarkDelete: () => void;
  onCancelDelete: () => void;
  onImageError: () => void;
}

export default function OrganizationPhotoCard({
  existingUrl,
  pendingFile,
  markedForDelete,
  fileError,
  disabled,
  labels,
  onFileSelected,
  onClearPending,
  onMarkDelete,
  onCancelDelete,
  onImageError,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const previewUrl = useMemo(
    function buildPreviewUrl() {
      if (!pendingFile) return null;
      return URL.createObjectURL(pendingFile);
    },
    [pendingFile],
  );

  useEffect(
    function revokePreviewOnChange() {
      return function cleanup() {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
      };
    },
    [previewUrl],
  );

  const displayUrl = pendingFile ? previewUrl : markedForDelete ? null : existingUrl;

  const accept = ALLOWED_PHOTO_MIME_TYPES.join(",");

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    if (inputRef.current) inputRef.current.value = "";
    if (file) onFileSelected(file);
  }

  function openPicker() {
    inputRef.current?.click();
  }

  const canDelete = existingUrl !== null && !pendingFile && !markedForDelete;
  const canCancel = pendingFile !== null || markedForDelete;

  return (
    <div className="p-7 radius-3xl surface-base-fill flex flex-col gap-4">
      <h2 className="text-body-bold-lg content-base-primary pb-3 border-b surface-component-stroke">{labels.title}</h2>
      <div className="flex items-center gap-5 flex-wrap">
        <div className="w-24 aspect-square radius-lg surface-base-fill surface-base-stroke border flex items-center justify-center overflow-hidden shrink-0">
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="organization photo"
              className="w-full h-full object-cover"
              onError={onImageError}
            />
          ) : (
            <Gallery size={32} color="currentColor" className="content-base-low" />
          )}
        </div>
        <div className="flex flex-col gap-2 flex-1 min-w-[240px]">
          <p className="text-body-regular-sm content-base-secondary">{labels.hint}</p>
          <div className="flex gap-2 flex-wrap">
            <Button type="button" variant="secondary" size="md" onClick={openPicker} disabled={disabled}>
              <DocumentUpload size={18} color="currentColor" />
              <span>{existingUrl && !markedForDelete ? labels.replace : labels.upload}</span>
            </Button>
            {canDelete && (
              <Button type="button" variant="destructive" size="md" onClick={onMarkDelete} disabled={disabled}>
                <Trash size={18} color="currentColor" />
                <span>{labels.delete}</span>
              </Button>
            )}
            {canCancel && (
              <Button
                type="button"
                variant="tertiary"
                size="md"
                onClick={pendingFile ? onClearPending : onCancelDelete}
                disabled={disabled}>
                <CloseCircle size={18} color="currentColor" />
                <span>{labels.cancel}</span>
              </Button>
            )}
          </div>
          {fileError && <p className="text-body-regular-xs content-action-negative">{fileError}</p>}
          {!displayUrl && !fileError && <p className="text-body-regular-xs content-base-low">{labels.empty}</p>}
        </div>
      </div>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handleChange} />
    </div>
  );
}

